import {Stack, Textarea, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {getUserMessage} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {GatewayType} from './InstrumentGatewayConstants';
import {createGateway} from './InstrumentGatewayDAO';

export interface CreateSnowflakeGatewayModalProps {
  onRequestClose: () => void;
  onSuccess?: () => void;
}

const GENERIC_VALIDATION_FAILURE_MESSAGE = 'Validation failed. Please check your input and try again.';

export function CreateSnowflakeGatewayModal(props: CreateSnowflakeGatewayModalProps): JSX.Element {
  const {onRequestClose, onSuccess} = props;
  const [error, setError] = useState<string | null>(null);

  const [gatewayName, setGatewayName] = useState('');
  const [snowflakeAccount, setSnowflakeAccount] = useState('');
  const [snowflakeUser, setSnowflakeUser] = useState('');
  const [privateKeyPem, setPrivateKeyPem] = useState('');
  const [snowflakeWarehouse, setSnowflakeWarehouse] = useState('');

  const allRequiredFieldsFilled = Boolean(
    gatewayName.trim() && snowflakeAccount.trim() && snowflakeUser.trim() && privateKeyPem.trim()
  );

  function buildGatewayFields(): Record<string, string> {
    const fields: Record<string, string> = {
      connection_mode: 'DIRECT',
      snowflake_account: snowflakeAccount.trim(),
      snowflake_user: snowflakeUser.trim(),
      private_key_pem: privateKeyPem.trim(),
    };
    if (snowflakeWarehouse.trim()) {
      fields.snowflake_warehouse = snowflakeWarehouse.trim();
    }
    return fields;
  }

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Configure gateway',
          disabled: !allRequiredFieldsFilled,
          submit: true,
        },
      ]}
      title="Configure Snowflake gateway"
      onRequestClose={onRequestClose}
      closeable={true}
      bannerProps={error != null ? {bannerType: InlineBannerType.ERROR, text: error} : null}
      onRequestSubmit={async () => {
        setError(null);
        try {
          await withLoadingScreen(
            createGateway(gatewayName, GatewayType.SNOWFLAKE, buildGatewayFields()),
            'Setting up Snowflake gateway...'
          );
          onSuccess?.();
          onRequestClose();
        } catch (err: unknown) {
          const userMessage = getUserMessage(err);
          if (userMessage == null) {
            setError(GENERIC_VALIDATION_FAILURE_MESSAGE);
            throw err;
          }
          setError(userMessage);
        }
      }}
    >
      <Stack spacing="md">
        <TextInput
          label="Name"
          required={true}
          value={gatewayName}
          onChange={(e) => {
            setGatewayName(e.currentTarget.value);
          }}
        />
        <TextInput
          label="Snowflake account"
          description="e.g. <org-name>-<account>"
          required={true}
          value={snowflakeAccount}
          onChange={(e) => {
            setSnowflakeAccount(e.currentTarget.value);
          }}
        />
        <TextInput
          label="Snowflake user"
          required={true}
          value={snowflakeUser}
          onChange={(e) => {
            setSnowflakeUser(e.currentTarget.value);
          }}
        />
        <Textarea
          label="Private key (PEM)"
          description="RSA private key in PEM format for key-pair authentication"
          required={true}
          minRows={4}
          autosize={true}
          value={privateKeyPem}
          onChange={(e) => {
            setPrivateKeyPem(e.currentTarget.value);
          }}
        />
        <TextInput
          label="Warehouse (optional)"
          value={snowflakeWarehouse}
          onChange={(e) => {
            setSnowflakeWarehouse(e.currentTarget.value);
          }}
        />
      </Stack>
    </Modal>
  );
}
